import { API_ORIGIN, apiUrl } from "./apiOrigin";
/**
 * SOPStaffGrid.js
 * 
 * Reusable component: Staff (rows) × SOP (columns) grid with read/date toggle.
 * Used in: HeadOffice (SOP Compliance) and SinglePharmacy views.
 * 
 * Props:
 *   pharmacyId (string, required) - UUID of pharmacy
 *   asOfDate (ISO string, required) - Date for which to show assignment status
 *   showDates (boolean, optional) - Toggle between status mode and date mode
 *   onBack (function, optional) - Callback when user clicks back button
 *   masked (boolean, optional) - Apply name masking (for admin demo mode)
 */

import React, { useState, useEffect, useMemo, useRef } from "react";

// Helper: format ISO date to "DD MMM YYYY"
function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
}

// Helper: percentage to color (red → amber → green)
function pctColor(pct) {
  if (pct === null || pct === undefined) return "#F5F5F5";
  const hue = Math.max(0, Math.min(120, pct / 100 * 120));
  return `hsl(${hue}, 75%, 82%)`;
}
function pctTextColor(pct) {
  if (pct === null || pct === undefined) return "#8C8C8C";
  return pct < 50 ? "#820014" : pct < 80 ? "#873800" : "#064e3b";
}

// Helper: apply name masking
function displayName(name, masked) {
  if (!masked || !name) return name;
  return name.replace(/[a-z]/g, "x").replace(/[A-Z]/g, "X");
}

// Branch display names for copied readings (the grid sends branch db ids).
function branchName(dbName) {
  try {
    const members = JSON.parse(localStorage.getItem("members") || "[]");
    const m = (Array.isArray(members) ? members : []).find(x => String(x.pharmacyId) === String(dbName));
    return m && (m.pharmacyName || m.name) || dbName || "another branch";
  } catch {
    return dbName || "another branch";
  }
}

// Badge for a reading copied from another branch (SOP replication).
function CopiedBadge() {
  return <span aria-label="Copied from another branch" style={{
    display: "inline-block",
    marginLeft: 4,
    padding: "0 5px",
    borderRadius: 8,
    background: "#F9F0FF",
    color: "#531DAB",
    border: "1px solid #D3ADF7",
    fontSize: 10,
    fontWeight: 700,
    lineHeight: "15px",
    verticalAlign: "middle"
  }}>
      ↻ copied
    </span>;
}

// Use assigned reading cards for both staff percentages and the site total.
// Missing cells and zero-assignment cells (shown as dashes) do not contribute.
function assignmentCompliance(cells, titles, staffNames) {
  let shouldHaveRead = 0;
  let didRead = 0;
  for (const staffName of staffNames) {
    for (const title of titles) {
      const cell = cells?.[title]?.[staffName];
      if (!cell || !(cell.shouldHaveRead > 0)) continue;
      shouldHaveRead += cell.shouldHaveRead;
      didRead += cell.didRead || 0;
    }
  }
  const pct = shouldHaveRead > 0 ? Math.round(didRead / shouldHaveRead * 1000) / 10 : null;
  return {
    shouldHaveRead,
    didRead,
    pct
  };
}
export default function SOPStaffGrid({
  pharmacyId,
  asOfDate,
  showDates = false,
  onBack,
  masked = false
}) {
  const [gridData, setGridData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [localShowDates, setLocalShowDates] = useState(showDates);
  const tokenRef = useRef(null);
  useEffect(() => {
    tokenRef.current = localStorage.getItem("authToken");
  }, []);

  // Load grid data from backend
  useEffect(() => {
    const loadGrid = async () => {
      setLoading(true);
      setError("");
      try {
        const token = tokenRef.current || localStorage.getItem("authToken");
        if (!token) throw new Error("Not authenticated");
        const res = await fetch(apiUrl("/sop_compliance_grid"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({
            asOfDate: asOfDate,
            groupBy: "pharmacy",
            pharmacyId: pharmacyId
          })
        });
        if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
        const json = await res.json();
        setGridData(json);
        // DEBUG
        console.log("SOPStaffGrid response:", json);
      } catch (e) {
        setError(e.message || String(e));
      } finally {
        setLoading(false);
      }
    };
    loadGrid();
  }, [pharmacyId, asOfDate]);

  // Extract data
  const sopTitles = gridData?.sopTitles || [];
  const sopMeta = gridData?.sopMeta || {};
  const groups = gridData?.groups || [];

  // Build category groupings
  const titlesByCategory = useMemo(() => {
    const map = new Map();
    for (const t of sopTitles) {
      const cat = sopMeta[t]?.category || "(uncategorised)";
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(t);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [sopTitles, sopMeta]);

  // For detail grid mode, we expect exactly one group (the selected pharmacy)
  const group = groups.length > 0 ? groups[0] : null;
  const staffNames = group?.headers || [];
  const cells = group?.cells || {};
  const overall = assignmentCompliance(cells, sopTitles, staffNames);

  // Render cell content based on mode
  function renderCell(sopTitle, staffName) {
    const cell = cells?.[sopTitle]?.[staffName];

    // No assignment
    if (!cell) {
      return {
        symbol: "—",
        bgColor: "#F5F5F5",
        textColor: "#8C8C8C"
      };
    }
    if (localShowDates) {
      // Date mode: show read date (green) or last reminder date (red)
      if (cell.readAndSignedAt) {
        return {
          symbol: fmtDate(cell.readAndSignedAt),
          bgColor: "#F6FFED",
          textColor: "#15803d"
        };
      } else if (cell.lastReminderSentAt) {
        return {
          symbol: fmtDate(cell.lastReminderSentAt),
          bgColor: "#FFF1F0",
          textColor: "#A8071A"
        };
      } else {
        return {
          symbol: "Unread",
          bgColor: "#FFF1F0",
          textColor: "#A8071A"
        };
      }
    } else {
      // Status mode: tick/cross based on didRead vs shouldHaveRead
      const didRead = cell.didRead ?? 0;
      const shouldHaveRead = cell.shouldHaveRead ?? 0;

      // If didRead equals shouldHaveRead (all read), show tick
      // If didRead is 0 (none read), show cross
      // Otherwise show partial symbol
      if (shouldHaveRead === 0) {
        return {
          symbol: "—",
          bgColor: "#F5F5F5",
          textColor: "#8C8C8C"
        };
      } else if (didRead >= shouldHaveRead) {
        return {
          symbol: "✓",
          bgColor: "#F6FFED",
          textColor: "#15803d"
        };
      } else if (didRead > 0) {
        return {
          symbol: "◐",
          bgColor: "#FFF7E6",
          textColor: "#AD4E00"
        };
      } else {
        return {
          symbol: "✗",
          bgColor: "#FFF1F0",
          textColor: "#A8071A"
        };
      }
    }
  }

  // Render category roll-up row
  function renderCategoryRow(category, titles) {
    return <tr key={`cat-${category}`} style={{
      background: "#fafafa"
    }}>
        <td style={{
        padding: "6px 10px",
        fontWeight: 600,
        color: "#434343",
        borderBottom: "1px solid #F0F0F0",
        background: "#fafafa",
        position: "sticky",
        left: 0,
        zIndex: 3
      }}>
          {category} <span style={{
          fontSize: 10,
          color: "#8C8C8C",
          fontWeight: 400
        }}>(roll-up)</span>
        </td>
        {staffNames.map(staffName => {
        const {
          pct
        } = assignmentCompliance(cells, titles, [staffName]);
        return <td key={`${category}-${staffName}`} style={{
          padding: "4px 6px",
          textAlign: "center",
          borderBottom: "1px solid #F0F0F0",
          background: pctColor(pct),
          color: pctTextColor(pct),
          fontWeight: 600,
          fontSize: 11,
          minWidth: 80
        }}>
              {pct === null ? "—" : `${pct}%`}
            </td>;
      })}
      </tr>;
  }
  return <div style={{
    padding: 16,
    background: "#FAFAFA"
  }}>
      {/* Header */}
      <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 16
    }}>
        <div>
          {onBack && <button onClick={onBack} style={{
          padding: "6px 12px",
          background: "#fff",
          border: "1px solid #D9D9D9",
          borderRadius: 4,
          cursor: "pointer",
          fontSize: 13,
          color: "#434343",
          marginBottom: 8
        }}>
              ← Back
            </button>}
          <h3 style={{
          margin: "0 0 4px 0",
          color: "#262626",
          fontSize: 16
        }}>
            {group?.label ? displayName(group.label, masked) : "Pharmacy"} — Staff SOP Compliance
          </h3>
          <div style={{
          fontSize: 13,
          color: "#5D5D5D"
        }}>
            {staffNames.length} staff · {sopTitles.length} SOPs
            {group && ` · ${overall.didRead}/${overall.shouldHaveRead} read (${overall.pct === null ? "—" : `${overall.pct}%`})`}
          </div>
          {group && Object.values(group.cells || {}).some(row => Object.values(row || {}).some(c => c?.replications?.length)) && <div style={{
          fontSize: 12,
          color: "#531DAB",
          marginTop: 4
        }}>
              <CopiedBadge /> = read at another branch and copied here. Hover the cell for where and when.
            </div>}
        </div>

        {/* Toggle button */}
        <button onClick={() => setLocalShowDates(!localShowDates)} style={{
        padding: "6px 12px",
        background: localShowDates ? "#3b82f6" : "#fff",
        color: localShowDates ? "#fff" : "#434343",
        border: "1px solid #D9D9D9",
        borderRadius: 4,
        cursor: "pointer",
        fontSize: 13,
        fontWeight: 500
      }}>
          {localShowDates ? "Show Status" : "Show Dates"}
        </button>
      </div>

      {/* Error state */}
      {error && <div style={{
      padding: 16,
      color: "#CF1322",
      background: "#FFF1F0",
      border: "1px solid #FFCCC7",
      borderRadius: 6,
      marginBottom: 16
    }}>
          Error: {error}
        </div>}

      {/* Loading state */}
      {loading && <div style={{
      padding: 24,
      color: "#5D5D5D"
    }}>Loading grid…</div>}

      {/* Empty state */}
      {!loading && !error && (!group || staffNames.length === 0 || sopTitles.length === 0) && <div style={{
      padding: 24,
      color: "#5D5D5D",
      textAlign: "center",
      background: "#fff",
      border: "1px solid #F0F0F0",
      borderRadius: 8
    }}>
          No SOP assignments found for this pharmacy.
        </div>}

      {/* Grid table with horizontal scrolling */}
      {!loading && !error && group && <div style={{
      background: "#fff",
      border: "1px solid #F0F0F0",
      borderRadius: 8,
      overflow: "hidden"
    }}>
          <div style={{
        overflowX: "auto",
        overflowY: "visible",
        maxHeight: "70vh"
      }}>
            <table style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: 12,
          minWidth: "fit-content"
        }}>
              <thead>
                <tr>
                  <th style={{
                padding: "6px 10px",
                textAlign: "left",
                borderBottom: "1px solid #F0F0F0",
                background: "#fff",
                fontWeight: 600,
                color: "#434343",
                position: "sticky",
                left: 0,
                zIndex: 4,
                minWidth: 250
              }}>
                    SOP
                  </th>
                  {staffNames.map(staffName => <th key={staffName} style={{
                padding: "6px 8px",
                textAlign: "center",
                borderBottom: "1px solid #F0F0F0",
                minWidth: 100,
                fontWeight: 600,
                color: "#434343",
                background: "#FAFAFA",
                fontSize: 11
              }}>
                      {displayName(staffName, masked)}
                    </th>)}
                </tr>
              </thead>

              <tbody>
                {titlesByCategory.map(([category, titles]) => <React.Fragment key={category}>
                    {/* Category roll-up */}
                    {renderCategoryRow(category, titles)}

                    {/* Individual SOP rows */}
                    {titles.map(sopTitle => <tr key={sopTitle}>
                        <td style={{
                  padding: "5px 10px 5px 10px",
                  borderBottom: "1px solid #F5F5F5",
                  color: "#434343",
                  background: "#fff",
                  position: "sticky",
                  left: 0,
                  zIndex: 3,
                  fontSize: 12,
                  fontWeight: 500,
                  minWidth: 250
                }}>
                          {sopTitle}
                        </td>
                        {staffNames.map(staffName => {
                  const {
                    symbol,
                    bgColor,
                    textColor
                  } = renderCell(sopTitle, staffName);
                  return <td key={`${sopTitle}-${staffName}`} title={(cells?.[sopTitle]?.[staffName]?.replications || []).map(r => `Copied reading: read at ${displayName(branchName(r.sourceBranch), masked)} on ${fmtDate(r.originalReadAt)}; copied here ${fmtDate(r.replicatedAt)}`).join("\n") || undefined} style={{
                    padding: "6px 4px",
                    textAlign: "center",
                    borderBottom: "1px solid #F5F5F5",
                    background: bgColor,
                    color: textColor,
                    fontSize: localShowDates ? 10 : 14,
                    fontWeight: localShowDates ? 400 : 600,
                    minWidth: 100
                  }}>
                              {symbol}
                              {cells?.[sopTitle]?.[staffName]?.replications?.length > 0 && <CopiedBadge />}
                            </td>;
                })}
                      </tr>)}
                  </React.Fragment>)}
              </tbody>
            </table>
          </div>
        </div>}
    </div>;
}
